import { parseDate, createCalendar } from '@internationalized/date';
import { useRangeCalendar } from '@react-aria/calendar';
import { useRangeCalendarState } from '@react-stately/calendar';
import type { DateValue } from '@react-stately/calendar';
import * as React from 'react';
import { I18nProvider, useLocale } from 'react-aria';
import { CalendarGrid } from './calendar-grid';
import { CalendarHeader } from './calendar-header';
import { cn } from '@utils';

export type IDateRange = {
	start: string;
	end: string;
};

export type IRangeDatePickerProps = {
	value?: IDateRange | null;
	defaultValue?: IDateRange | null;
	onChange?: (value: IDateRange) => void;
	notBefore?: string;
	notAfter?: string;
	isDisabled?: boolean;
	isReadOnly?: boolean;
	isDateUnavailable?: (date: string) => boolean;
	locale?: string;
	autoFocus?: boolean;
	className?: string;
	'data-testid'?: string;
};

type DateValueRange = { start: DateValue; end: DateValue };

const parseControlledRange = (raw: IDateRange | null | undefined): DateValueRange | null | undefined => {
	if (raw === undefined) return undefined;
	if (raw === null) return null;
	return { start: parseDate(raw.start), end: parseDate(raw.end) };
};

const parseUncontrolledRange = (raw: IDateRange | null | undefined): DateValueRange | undefined => {
	if (!raw) return undefined;
	return { start: parseDate(raw.start), end: parseDate(raw.end) };
};

const parseOptional = (raw: string | undefined): DateValue | undefined => {
	return raw ? parseDate(raw) : undefined;
};

type InnerProps = Omit<IRangeDatePickerProps, 'locale'>;

const RangeDatePickerInner: React.VoidFunctionComponent<InnerProps> = ({
	value,
	defaultValue,
	onChange,
	notBefore,
	notAfter,
	isDisabled,
	isReadOnly,
	isDateUnavailable,
	autoFocus,
	className,
	'data-testid': dataTestId = 'range-date-picker',
}) => {
	const { locale } = useLocale();
	const ref = React.useRef<HTMLDivElement | null>(null);

	const controlledValue = React.useMemo(() => parseControlledRange(value), [value]);
	const uncontrolledDefault = React.useMemo(() => parseUncontrolledRange(defaultValue), [defaultValue]);
	const minValue = React.useMemo(() => parseOptional(notBefore), [notBefore]);
	const maxValue = React.useMemo(() => parseOptional(notAfter), [notAfter]);

	const wrappedIsDateUnavailable = React.useMemo(() => {
		if (!isDateUnavailable) {
			return undefined;
		}
		return (date: DateValue) => isDateUnavailable(date.toString());
	}, [isDateUnavailable]);

	const handleChange = React.useCallback(
		(range: DateValueRange) => {
			onChange?.({ start: range.start.toString(), end: range.end.toString() });
		},
		[onChange]
	);

	const sharedProps = {
		value: controlledValue,
		defaultValue: uncontrolledDefault,
		onChange: handleChange,
		minValue,
		maxValue,
		isDisabled,
		isReadOnly,
		isDateUnavailable: wrappedIsDateUnavailable,
		autoFocus,
	};

	const state = useRangeCalendarState({
		...sharedProps,
		locale,
		createCalendar,
	});

	const {
		calendarProps: rootProps,
		prevButtonProps,
		nextButtonProps,
		title,
	} = useRangeCalendar(sharedProps, state, ref);

	return (
		<div {...rootProps} ref={ref} data-testid={dataTestId} className={cn('inline-flex flex-col p-4', className)}>
			<CalendarHeader title={title} prevButtonProps={prevButtonProps} nextButtonProps={nextButtonProps} />
			<CalendarGrid state={state} />
		</div>
	);
};

export const RangeDatePicker: React.VoidFunctionComponent<IRangeDatePickerProps> = ({ locale, ...rest }) => {
	if (locale) {
		return (
			<I18nProvider locale={locale}>
				<RangeDatePickerInner {...rest} />
			</I18nProvider>
		);
	}
	return <RangeDatePickerInner {...rest} />;
};
